import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Truck } from '../models/truck.model';
import { GranulometryLimits, GranulometryAlert, DEFAULT_GRANULOMETRY_LIMITS } from '../models/granulometry-limits.model';

@Injectable({
    providedIn: 'root',
})
export class GranulometryAlertService {
    private limits: GranulometryLimits = DEFAULT_GRANULOMETRY_LIMITS;
    private alertsSubject = new BehaviorSubject<GranulometryAlert[]>([]);
    public alerts$ = this.alertsSubject.asObservable();

    constructor() {}

    /**
     * Obtiene los límites actuales
     */
    getLimits(): GranulometryLimits {
        return this.limits;
    }

    /**
     * Actualiza los límites de granulometría
     */
    setLimits(limits: GranulometryLimits): void {
        this.limits = limits;
    }

    /**
     * Verifica si un camión está fuera de los límites
     */
    checkTruckLimits(truck: Truck): GranulometryAlert | null {
        if (!truck.granulometry) return null;

        const violations: string[] = [];
        let alertType: 'D80' | 'D50' | 'D20' | 'MULTIPLE' = 'D80';
        let severity: 'warning' | 'critical' = 'warning';

        // Verificar D80
        if (truck.granulometry.d80 < this.limits.d80.min || truck.granulometry.d80 > this.limits.d80.max) {
            violations.push(`D80: ${truck.granulometry.d80}mm (límites: ${this.limits.d80.min}-${this.limits.d80.max}mm)`);
            alertType = 'D80';
        }

        // Verificar D50
        if (truck.granulometry.d50 < this.limits.d50.min || truck.granulometry.d50 > this.limits.d50.max) {
            violations.push(`D50: ${truck.granulometry.d50}mm (límites: ${this.limits.d50.min}-${this.limits.d50.max}mm)`);
            if (violations.length > 1) alertType = 'MULTIPLE';
            else alertType = 'D50';
        }

        // Verificar D20
        if (truck.granulometry.d20 < this.limits.d20.min || truck.granulometry.d20 > this.limits.d20.max) {
            violations.push(`D20: ${truck.granulometry.d20}mm (límites: ${this.limits.d20.min}-${this.limits.d20.max}mm)`);
            if (violations.length > 1) alertType = 'MULTIPLE';
            else alertType = 'D20';
        }

        if (violations.length === 0) return null;

        // Severidad crítica si hay múltiples violaciones
        if (violations.length >= 2) {
            severity = 'critical';
        }

        return {
            truckId: truck.id,
            plate: truck.plate,
            detectionDate: truck.detectionDate,
            alertType: alertType,
            message: violations.join(', '),
            severity: severity,
            acknowledged: false
        };
    }

    /**
     * Verifica múltiples camiones y genera alertas
     */
    checkMultipleTrucks(trucks: Truck[]): GranulometryAlert[] {
        const alerts: GranulometryAlert[] = [];

        trucks.forEach(truck => {
            const alert = this.checkTruckLimits(truck);
            if (alert) {
                alerts.push(alert);
            }
        });

        this.alertsSubject.next(alerts);
        return alerts;
    }

    /**
     * Obtiene el número de alertas no atendidas
     */
    getUnacknowledgedAlertsCount(): number {
        return this.alertsSubject.value.filter(a => !a.acknowledged).length;
    }

    /**
     * Obtiene todas las alertas actuales
     */
    getCurrentAlerts(): GranulometryAlert[] {
        return this.alertsSubject.value;
    }

    /**
     * Marca una alerta como atendida
     */
    acknowledgeAlert(truckId: string): void {
        const alerts = this.alertsSubject.value;
        const alert = alerts.find(a => a.truckId === truckId);
        if (alert) {
            alert.acknowledged = true;
            this.alertsSubject.next([...alerts]);
        }
    }

    /**
     * Determina si un truck tiene alertas
     */
    hasAlert(truck: Truck): boolean {
        return this.checkTruckLimits(truck) !== null;
    }

    /**
     * Obtiene el tipo de alerta (severity class)
     */
    getAlertClass(truck: Truck): string {
        const alert = this.checkTruckLimits(truck);
        if (!alert) return '';
        return alert.severity === 'critical' ? 'alert-critical' : 'alert-warning';
    }
}
